// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Authentication Service
 *
 * Manages authentication state via MSAL.js (Azure Entra External ID / CIAM).
 * Token acquisition, refresh, and caching are handled by MSAL.
 * User profile data is hydrated from the /api/v1/me endpoint and stored in
 * localStorage for fast synchronous reads by UI components.
 */

import { msalInstance, loginRequest, getTokenAsync } from '../config/authConfig';
import { get } from './api';
import { setUserData, getUserData, removeUserData } from './tokenStorage';
import { ENDPOINTS } from '../utils/constants';
import type { UserData } from '../types';
import type { MeResponse } from '../types';
import type { UserRole } from '../types';

// Re-export getTokenAsync from authConfig for consumers that import from authService
export { getTokenAsync };

// ---------------------------------------------------------------------------
// Login / Logout
// ---------------------------------------------------------------------------

/**
 * Trigger a CIAM login redirect.
 * Call this from the Login page or when the user needs to re-authenticate.
 */
export const login = async (): Promise<void> => {
  await msalInstance.loginRedirect(loginRequest);
};

/**
 * Log out the current user.
 * Clears local user data and redirects to the CIAM logout endpoint.
 */
export const logout = async (): Promise<void> => {
  removeUserData();
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: `${window.location.origin}/login`,
  });
};

// ---------------------------------------------------------------------------
// Authentication state
// ---------------------------------------------------------------------------

/**
 * Check if a user is authenticated (MSAL has an active account).
 * Synchronous — checks the in-memory MSAL cache, not the network.
 */
export const isAuthenticated = (): boolean => {
  return msalInstance.getActiveAccount() !== null;
};

// ---------------------------------------------------------------------------
// User data (hydrated from /api/v1/me, stored in localStorage)
// ---------------------------------------------------------------------------

/**
 * Get stored user data (sourced from /me response, not token decode)
 */
export const getUser = (): UserData | null => {
  return getUserData();
};

/**
 * Fetch current user profile from /api/v1/me and persist it to localStorage.
 * Called after login redirect returns to hydrate the full user context
 * (role, permissions, org) that the CIAM token doesn't carry.
 */
export const fetchAndStoreMe = async (): Promise<UserData | null> => {
  try {
    const data = await get<MeResponse>(ENDPOINTS.ME);
    if (data) {
      const userData: UserData = {
        user_id: data.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        org_id: data.organization_id,
        org_name: data.organization?.preferredName ?? null,
        default_role: data.role as UserRole,
        user_group_ids: [],
        effective_permissions: data.permissions ?? [],
      };
      setUserData(userData);
      return userData;
    }
    return null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Role / permission helpers (read from stored user data)
// ---------------------------------------------------------------------------

/**
 * Get user's role
 */
export const getUserRole = (): UserRole | null => {
  const user = getUser();
  return user?.default_role || null;
};

/**
 * Check if user has a specific permission string (e.g. "iam.user.create")
 */
export const hasPermission = (permission: string): boolean => {
  const user = getUser();
  if (!user?.effective_permissions) return false;
  return user.effective_permissions.includes(permission);
};

/**
 * Check if user has specific role or higher.
 * Role hierarchy: operator < operations_manager < admin.
 * Executive is orthogonal (read-only across all scopes).
 */
export const hasRole = (role: UserRole): boolean => {
  const userRole = getUserRole();
  const hierarchy: UserRole[] = ['operator', 'operations_manager', 'admin'];

  if (!userRole) return false;

  if (role === 'executive' || userRole === 'executive') {
    return userRole === role;
  }

  const userLevel = hierarchy.indexOf(userRole);
  const requiredLevel = hierarchy.indexOf(role);

  if (userLevel === -1 || requiredLevel === -1) return false;
  return userLevel >= requiredLevel;
};

/**
 * Check if user is an admin
 */
export const isAdmin = (): boolean => {
  const user = getUser();
  return user?.default_role === 'admin';
};
